import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AmpelBadge } from "@/components/AmpelBadge"
import { Button, Select, Input, Textarea } from "@/components/ui"
import { useToast, fehlerNachricht } from "@/lib/toast"
import {
  createPruefung,
  deletePruefung,
  duplizierePruefung,
  erstellePruefungServiceticket,
  fetchAnlagen,
  fetchPruefung,
  fetchPruefungsarten,
  updatePruefung,
  type PruefungEingabe,
} from "@/lib/api"

const LEER: PruefungEingabe = {
  pruefungsartId: 0,
  komponenteId: null,
  objektId: null,
  anlageId: null,
  pruefungsdatum: null,
  bestanden: null,
  nochGueltig: true,
  bemerkung: null,
  protokollPaperlessId: null,
}

/** Prüfungs-Detailformular (AP-3.4) — Ablaufdatum/Ampel werden serverseitig berechnet. */
export function PruefungDetailPage() {
  const params = useParams<{ id: string }>()
  const istNeu = params.id === "neu"
  const pruefungId = istNeu ? null : Number(params.id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [form, setForm] = useState<PruefungEingabe>(LEER)

  const { data: pruefung } = useQuery({
    queryKey: ["pruefungen", pruefungId],
    queryFn: () => fetchPruefung(pruefungId!),
    enabled: pruefungId !== null,
  })
  const { data: arten } = useQuery({ queryKey: ["pruefungsarten"], queryFn: fetchPruefungsarten })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  useEffect(() => {
    if (pruefung) setForm(pruefung)
  }, [pruefung])

  const speichern = useMutation({
    mutationFn: () =>
      istNeu ? createPruefung(form) : updatePruefung(pruefungId!, form),
    onSuccess: (ergebnis) => {
      queryClient.invalidateQueries({ queryKey: ["pruefungen"] })
      toast.success("Prüfung gespeichert.")
      if (istNeu) navigate(`/maschinen/pruefungen/${ergebnis.id}`)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Prüfung konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deletePruefung(pruefungId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pruefungen"] })
      toast.success("Prüfung gelöscht.")
      navigate("/maschinen/pruefungen")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Prüfung konnte nicht gelöscht werden.")),
  })

  const duplizieren = useMutation({
    mutationFn: () => duplizierePruefung(pruefungId!),
    onSuccess: (neu) => {
      toast.success("Prüfung dupliziert.")
      navigate(`/maschinen/pruefungen/${neu.id}`)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Prüfung konnte nicht dupliziert werden.")),
  })

  const serviceticket = useMutation({
    mutationFn: () => erstellePruefungServiceticket(pruefungId!),
    onSuccess: () => toast.success("Serviceticket erstellt."),
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Serviceticket konnte nicht erstellt werden.")),
  })

  return (
    <div>
      <nav className="mb-4 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link
          to="/maschinen/pruefungen"
          className="rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          ← Zurück zu Prüfungen
        </Link>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {istNeu ? "Neue Prüfung" : `Prüfung #${pruefungId}`}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          02-N — Ablaufdatum wird aus Prüfungsdatum + Intervall der Prüfungsart berechnet (§5).
        </p>
        {pruefung && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="text-slate-600">Status:</span>
            <AmpelBadge ampel={pruefung.ampel} />
            <span className="text-slate-500">Ablaufdatum: {pruefung.ablaufdatum ?? "—"}</span>
          </div>
        )}
      </header>

      <section className="max-w-xl rounded-lg border border-slate-200 bg-white p-6">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <label className="block text-sm">
            Prüfungsart
            <Select
              required
              value={form.pruefungsartId || ""}
              onChange={(e) => setForm({ ...form, pruefungsartId: Number(e.target.value) })}
              className="mt-1"
            >
              <option value="">— auswählen —</option>
              {arten?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.pruefungsart}
                </option>
              ))}
            </Select>
          </label>
          <label className="block text-sm">
            Anlage
            <Select
              value={form.anlageId ?? ""}
              onChange={(e) =>
                setForm({ ...form, anlageId: e.target.value ? Number(e.target.value) : null })
              }
              className="mt-1"
            >
              <option value="">—</option>
              {anlagen?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bezeichnung}
                </option>
              ))}
            </Select>
          </label>
          <label className="block text-sm">
            Prüfungsdatum
            <Input
              type="date"
              value={form.pruefungsdatum ?? ""}
              onChange={(e) => setForm({ ...form, pruefungsdatum: e.target.value || null })}
              className="mt-1"
            />
          </label>
          <label className="block text-sm">
            Ergebnis
            <Select
              value={form.bestanden ?? ""}
              onChange={(e) => setForm({ ...form, bestanden: e.target.value || null })}
              className="mt-1"
            >
              <option value="">—</option>
              <option value="bestanden">bestanden</option>
              <option value="mit_maengeln">mit Mängeln</option>
              <option value="nicht_bestanden">nicht bestanden</option>
            </Select>
          </label>
          <label className="block text-sm">
            Bemerkung
            <Textarea
              value={form.bemerkung ?? ""}
              onChange={(e) => setForm({ ...form, bemerkung: e.target.value })}
              className="mt-1"
              rows={3}
            />
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              Speichern
            </Button>
            {!istNeu && (
              <>
                <Button type="button" variant="secondary" onClick={() => duplizieren.mutate()}>
                  Prüfung duplizieren
                </Button>
                <Button type="button" variant="secondary" onClick={() => serviceticket.mutate()}>
                  Serviceticket erstellen
                </Button>
                <Button type="button" variant="destructive" onClick={() => loeschen.mutate()}>
                  Löschen
                </Button>
              </>
            )}
          </div>
          {serviceticket.isSuccess && (
            <p className="text-sm text-green-700">
              Serviceticket erstellt: {serviceticket.data.ticketKey}
            </p>
          )}
        </form>
      </section>
    </div>
  )
}
